import { errorsSelector, setErrors } from '@src/features/auth';
import { BodyRegularSemiBold, BodySmallMedium } from '@src/fonts/typography/Typography';
import useTranslate from '@src/i18n/useTranslate';
import React, { FormEvent, useEffect } from 'react';
import styled from 'styled-components';
import { useAppSelector, useAppDispatch, useWidgetParam } from '../../core/hooks';
import { LOGIN, SIGNUP, FORGOT_PASSWORD } from './AuthDisplay';
import DotFalling from '../reusable/spinner/DotFalling';
import Input from './InputComponents/Input';
import {
  AuthContainer,
  AuthForm,
  AuthHeader,
  AuthHeaderTitle,
  AuthInputsGrid,
  AuthValidation,
  AuthButtonGroup,
  AuthPrimaryButton,
  AuthSecondaryButton,
  AuthPreviousStepButton,
  AuthInputContainer,
} from './StyledComponents/AuthStyled';
import { LayoutColors } from '@src/core/theme';
import { changeLocation } from '../../core/store';
import { device } from '@src/common/breakpoints';
import { setStepCheckout } from '@src/features/checkout';
import { getErrorMessage } from '@src/common';
import { extractValues } from '../../common/index';
import { login, sendingFormSelector } from '../../features/auth';
import { useWidgetId } from '../../core/IdProvider';
import InputPassword from './InputComponents/InputPassword';
import { track } from '@src/services/analytics';

interface LoginProps {
  setMode: (mode: string) => void;
  hasBackAction?: boolean;
}
const Login = (props: LoginProps) => {
  const { setMode, hasBackAction = false } = props;
  const id = useWidgetId();
  const dispatch = useAppDispatch();
  const backToCartPage = useWidgetParam('checkout-back-to');
  const redirect = () => dispatch(changeLocation(backToCartPage!));
  const { i18n, i18nElement } = useTranslate();
  const errors = useAppSelector(errorsSelector);
  const loginErrors = errors[LOGIN];
  const loading = useAppSelector(sendingFormSelector);

  useEffect(() => {
    track('loginAccess');

    dispatch(setStepCheckout({ id, value: LOGIN }));
    dispatch(setErrors(LOGIN, {}));
  }, []);

  const handleSubmit = (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();

    track('loginButtonClick');

    const values = extractValues(ev.target as HTMLFormElement);

    dispatch(
      login({
        email: values.email,
        password: values.password,
      }),
    );
  };

  const handleForgotPasswordClick = () => {
    setMode(FORGOT_PASSWORD);
  };

  return (
    <AuthContainer>
      <AuthForm key={LOGIN} onSubmit={handleSubmit} data-cy='authForm'>
        <AuthHeader>
          <AuthHeaderTitle>{i18n(`auth_accountHeaderTitle`)}</AuthHeaderTitle>
        </AuthHeader>
        <AuthInputsGrid>
          <AuthInputContainer>
            <Input name='email' type='email' label={i18n('auth_inputEmail')} errorContext={LOGIN} />
            <InputPassword name='password' label={i18n('auth_inputPassword')} errorContext={LOGIN} />
          </AuthInputContainer>
          <InfoContainer>
            <AuthValidation>{getErrorMessage(loginErrors, 'general')}</AuthValidation>
            <LinkRedirect onClick={handleForgotPasswordClick}>{i18nElement('auth_forgotPassword')}</LinkRedirect>
          </InfoContainer>
        </AuthInputsGrid>
        <AuthButtonGroup>
          <AuthPrimaryButton data-cy='loginButton'>
            {loading ? <DotFalling /> : i18nElement('auth_login')}
          </AuthPrimaryButton>
          <AuthSecondaryButton onClick={() => setMode(SIGNUP)}>
            {i18nElement('auth_registerMessage')}
          </AuthSecondaryButton>
          {hasBackAction ? (
            <AuthPreviousStepButton onClick={redirect}>{i18n('auth_goBackButton')}</AuthPreviousStepButton>
          ) : null}
        </AuthButtonGroup>
      </AuthForm>
    </AuthContainer>
  );
};

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  ${device.mobile} {
    flex-direction: row;
    align-items: center;
  }
`;

const LinkRedirect = styled.a`
  ${BodySmallMedium};
  margin-top: 0px;
  text-decoration: none;
  color: ${LayoutColors.justBlue};
  cursor: pointer;

  ${device.smallTablet} {
    ${BodyRegularSemiBold};
    margin-top: -14px;
    color: ${LayoutColors.justBlue};
  }
`;

export default Login;
